import { useEffect, useRef, useState } from 'react';
import { RefundMethod } from 'Models/AppConfigStore';
import { PaymentProviderTransactionDetails, setupPayment } from 'Services/Api/_HumanWritten/PaymentService';

export enum SetupPaymentError {
	NONE = 'NONE',
	TIMEOUT = 'TIMEOUT',
	NO_PAYMENT_REQUIRED = 'NO_PAYMENT_REQUIRED',
	/**
	 * The transaction's reservation hold has lapsed, so it can no longer be paid for —
	 * PreparePaymentContext's NotAcceptingPaymentException, returned as 403. Distinguished from
	 * GENERIC so callers can say "this expired, start again" instead of "something went wrong".
	 */
	EXPIRED = 'EXPIRED',
	GENERIC = 'GENERIC',
}

interface UseSetupPaymentParams {
	transactionId: string | null;
	redirectUrl: string;
	isEventBooking: boolean;
	inCheckIn: boolean;
	selectedRefundMethod?: RefundMethod;
}

interface UseSetupPaymentResult {
	transactionDetails: PaymentProviderTransactionDetails | null;
	error: SetupPaymentError;
	isLoading: boolean;
	noPaymentRedirectUrl: string | null;
}

const SETUP_TIMEOUT_MS = 30_000;

/**
 * Calls the backend's setup-payment endpoint exactly once per unique
 * transactionId. Uses refs for the in-flight state so React strict mode
 * re-renders don't cancel the request.
 */
export function useSetupPayment({
	transactionId,
	redirectUrl,
	isEventBooking,
	inCheckIn,
	selectedRefundMethod,
}: UseSetupPaymentParams): UseSetupPaymentResult {
	const [transactionDetails, setTransactionDetails] = useState<PaymentProviderTransactionDetails | null>(null);
	const [error, setError] = useState<SetupPaymentError>(SetupPaymentError.NONE);
	const [isLoading, setIsLoading] = useState(!!transactionId);
	const [noPaymentRedirectUrl, setNoPaymentRedirectUrl] = useState<string | null>(null);

	const lastCalledIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!transactionId) {
			// Null means the parent isn't ready for us to fire (e.g. waiting on a
			// prerequisite, or storage was cleared after a successful redirect).
			// Stay idle — the parent guards against a truly-missing transaction id
			// before rendering. Setting an error here would pop the modal during
			// the initial render-before-prerequisite-resolves window, and would
			// also clobber a NO_PAYMENT_REQUIRED success into a GENERIC error
			// during the post-success teardown.
			return undefined;
		}

		// Dedupe: setup-payment has real side effects server-side. A $0 booking gets
		// COMPLETED on the first call, and a second call hits NotAcceptingPaymentException
		// because the transaction is no longer IN_PROGRESS. The ref survives StrictMode's
		// fake-unmount/remount (and any other remount with the same txId), so we must NOT
		// reset it in the cleanup — doing so would let StrictMode fire a duplicate request.
		if (lastCalledIdRef.current === transactionId) {
			return undefined;
		}
		lastCalledIdRef.current = transactionId;

		const timeoutId = setTimeout(() => {
			setError(SetupPaymentError.TIMEOUT);
			setIsLoading(false);
		}, SETUP_TIMEOUT_MS);

		setIsLoading(true);
		setError(SetupPaymentError.NONE);
		setTransactionDetails(null);
		setNoPaymentRedirectUrl(null);

		setupPayment(transactionId, redirectUrl, isEventBooking, inCheckIn, selectedRefundMethod)
			.then(response => {
				clearTimeout(timeoutId);

				if (response.status === 201) {
					setNoPaymentRedirectUrl(response.data as unknown as string);
					setError(SetupPaymentError.NO_PAYMENT_REQUIRED);
				} else {
					setTransactionDetails(response.data as PaymentProviderTransactionDetails);
				}
				setIsLoading(false);
			})
			.catch((err: any) => {
				clearTimeout(timeoutId);

				// A 403 is PreparePaymentContext refusing a transaction that is no longer in
				// progress or whose hold has passed its expiry — the reservation lapsed. It is a
				// distinct, recoverable state ("start the change again"), not a generic failure.
				if (err?.response?.status === 403) {
					setError(SetupPaymentError.EXPIRED);
					setIsLoading(false);
					return;
				}

				// 409 from setup-payment is *not* a "no payment required" success.
				// Every 409 path in PaymentController originates from
				// PaymentNotRequiredFailureException — CompleteTransactionNoPaymentRequired's
				// catch-all that marks the transaction FAILED and the alterations
				// EXPIRED_RESERVATION before throwing. Treating it as success silently
				// navigates the user to the booking-success page, which then renders
				// blank because the booking has been destroyed. Surface it as a real
				// error so the user sees the payment-error modal and stays put.
				setError(SetupPaymentError.GENERIC);
				setIsLoading(false);
			});

		return () => {
			clearTimeout(timeoutId);
		};
		// Other params (redirectUrl, isEventBooking, etc.) are intentionally excluded — setup
		// should only fire once per unique transactionId. Re-running with the same transactionId
		// but different params would create duplicate setup calls on the backend.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [transactionId]);

	return {
		transactionDetails,
		error,
		// A transactionId that has not been requested yet is loading too. The effect above only runs
		// after the render that first receives the id, so without this that render reported "not
		// loading" — and a caller gating its payment form on this mounted the form for one render,
		// firing the form's own requests, only to unmount it again when the effect flipped
		// isLoading and remount it once setup-payment answered.
		isLoading: isLoading || (!!transactionId && lastCalledIdRef.current !== transactionId),
		noPaymentRedirectUrl,
	};
}
